import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as api from '../lib/api';
import { COVERS } from '../lib/media';
import { VIDEOS } from '../lib/media';
import HeroVideo from '../components/HeroVideo';

export default function Carnivore() {
  const [book, setBook] = useState<api.BookListItem | undefined>(undefined);

  useEffect(() => {
    api.books.list().then(list => {
      setBook(list.find(b => b.slug === 'fasting-carnivore' || (b.title?.toLowerCase().includes('fasting carnivore') && !b.title?.toLowerCase().includes('kitchen') && !b.title?.toLowerCase().includes('performance'))));
    }).catch(() => {});
  }, []);
  return (
    <>
      {/* Hero */}
      <section className="hero">
        <HeroVideo className="video-bg" src={VIDEOS.meatSteak} />
        <div className="video-overlay" />
        <div className="container book-page-hero-container">
          <motion.div
            className="hero-content text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="hero-label">Book One</span>
            <h1>Fasting Carnivore</h1>
            <p>Real Stories of Transformation. Real People Who Changed Everything.</p>
            <Link to="/join" className="btn btn-primary btn-lg">
              Join the Community
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Pain Point */}
      <section className="section">
        <div className="container">
          <div className="content-narrow">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="heading-section text-center">
                You've Tried Everything
              </h2>
              <div className="text-body-lg">
                <p className="mb-responsive-lg">
                  The calorie counting. The portion control. The "balanced" diets that left you hungry and exhausted. The apps that track every bite. The meal plans that require a spreadsheet to follow.
                </p>
                <p className="mb-responsive-lg">
                  You've white-knuckled your way through hunger, fought your own body, and lost. Again and again. Every time, the same pattern: Start strong. Struggle. Eventually give up. Blame yourself. Try again.
                </p>
                <p className="mb-responsive-lg book-page-emphasis">
                  And the worst part? You blamed yourself. You thought you lacked discipline. Willpower. Commitment.
                </p>
                <p className="mb-responsive-lg">
                  But what if the problem was never you? What if you were set up to fail from the start?
                </p>
                <p className="book-page-emphasis-accent">
                  What if there's a simpler approach that works with your body instead of against it—one that doesn't require constant hunger and endless tracking?
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* The Truth */}
      <section className="section section-light">
        <div className="container">
          <div className="content-narrow">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="heading-section text-center">
                A Different Approach
              </h2>
              <div className="text-body-lg">
                <p className="mb-responsive-lg">
                  What if eating didn't have to be complicated? What if you could stop the mental gymnastics and just... eat?
                </p>
                <p className="mb-responsive-lg">
                  No tracking. No counting. No endless meal planning. No guilt about "bad" foods. Just simple, satisfying food that keeps you full for hours—without the mental load.
                </p>
                <p className="mb-responsive-lg">
                  A growing movement of people are discovering carnivore eating—and finding a simplicity and clarity they never knew was possible. They're not fighting hunger. They're not obsessing over food.
                </p>
                <p className="book-page-emphasis">
                  They're eating meat. They're feeling satisfied. And it's finally working for them.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* The Book - Featured */}
      <section className="section section-white">
        <div className="container">
          <motion.div
            className="book-page-section-intro"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="section-label">The Book</span>
            <h2 className="heading-section">Stories of Transformation</h2>
          </motion.div>
          <div className="two-col book-page-two-col">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <img
                src={book?.coverUrl || COVERS.carnivore}
                alt="Fasting Carnivore Book"
                className="book-page-cover"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="book-page-feature-heading">
                Real People. Real Transformations.
              </h3>
              <p className="text-body-lg book-page-body-spaced">
                These aren't testimonials. They're complete stories—from the breaking point to the breakthrough.
                You'll feel like you're living these journeys alongside each person. You'll see yourself in their struggles. And you'll learn from how they overcame them.
              </p>
              <p className="text-body-lg book-page-body-spaced">
                Over 200 pages of real stories, real science, and practical insights. This is where understanding meets action. Included with your community membership.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* The Stories */}
      <section className="section section-light">
        <div className="container">
          <div className="section-header">
            <h2>Inside the Book</h2>
          </div>
          <div className="grid-3">
            <StoryCard
              name="Marcus"
              age="52"
              quote="I spent years counting every calorie. Now I just eat meat and live my life."
            />
            <StoryCard
              name="Elena"
              age="38"
              quote="The mental freedom of knowing exactly what I'm eating is incredible."
            />
            <StoryCard
              name="James"
              age="34"
              quote="I trained harder than anyone. Ate 'clean.' Nothing worked until this."
            />
            <StoryCard
              name="Sarah"
              age="45"
              quote="I was exhausted from the mental load of dieting. This simplified everything."
            />
            <StoryCard
              name="David"
              age="41"
              quote="I was skeptical. Then I tried it for 30 days. Now I tell everyone."
            />
            <StoryCard
              name="Maria"
              age="67"
              quote="At 67, they told me to slow down. I sped up instead."
            />
          </div>
          <p className="text-center text-subtitle book-page-mt-2">
            Plus 6 more transformation stories inside the book
          </p>
        </div>
      </section>

      {/* What You'll Learn */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2>What You'll Discover</h2>
          </div>
          <div className="content-narrow">
            <ul className="learn-list">
              <li>Why simplifying your diet to just animal foods can be surprisingly freeing</li>
              <li>The elimination diet approach: how removing complexity changes how you think about food</li>
              <li>Why you're always hungry—and how to finally feel satisfied without counting calories</li>
              <li>The science made simple: complex biology explained through stories you'll remember</li>
              <li>How to start without overwhelming yourself—practical first steps that work</li>
              <li>What to expect in the first 30 days: the good, the challenging, and the breakthrough</li>
              <li>How to handle social situations, family dinners, and people who don't understand</li>
              <li>The mental shifts that make this sustainable for life, not just another diet</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="section section-light">
        <div className="container">
          <div className="two-col book-page-two-col-narrow">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="book-page-for-heading">
                This Is For You If...
              </h3>
              <ul className="for-list">
                <li>You're curious about carnivore eating and want to learn more</li>
                <li>You're tired of complicated diets and conflicting advice</li>
                <li>You want a simple approach that doesn't require counting or tracking</li>
                <li>You're open to questioning conventional nutrition advice</li>
                <li>You don't want to do this alone</li>
                <li>You're ready to try something different</li>
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="book-page-not-for-heading">
                This Is NOT For You If...
              </h3>
              <ul className="not-for-list">
                <li>You're looking for a magic pill or overnight fix</li>
                <li>You're not willing to question what you've been taught</li>
                <li>You want someone else to do the work for you</li>
                <li>You're happy with how your current diet is working</li>
                <li>You think reading stories is a waste of time</li>
                <li>You're not ready to make any changes</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* The Bundle */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <span className="section-label">What You Get</span>
            <h2>The Carnivore Bundle</h2>
          </div>
          <div className="content-narrow">
            <div className="card book-page-card-padded">
              <ul className="bundle-list">
                <li>
                  <strong>The Book: Stories of Transformation</strong>
                  <span>Over 200 pages of real stories, real science, and real results</span>
                </li>
                <li>
                  <strong>Quick Reference Guide</strong>
                  <span>Cheat sheets, food lists, and protocols at a glance</span>
                </li>
                <li>
                  <strong>30-Day Action Guide</strong>
                  <span>Day-by-day tracker with journaling prompts and milestones</span>
                </li>
                <li>
                  <strong>Fasting Protocols</strong>
                  <span>How to combine carnivore eating with strategic fasting</span>
                </li>
                <li>
                  <strong>Carnivore Food List</strong>
                  <span>What to eat, what to avoid, and how to shop</span>
                </li>
                <li>
                  <strong>Glossary of Terms</strong>
                  <span>Every term explained in plain English</span>
                </li>
                <li>
                  <strong>Progress Worksheets</strong>
                  <span>Track your transformation from day one</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* About the Community */}
      <section className="section section-light">
        <div className="container">
          <div className="content-narrow text-center">
            <h2 className="heading-section">The Community</h2>
            <p className="text-body-lg book-page-body-mb">
              The hardest part of carnivore isn't the food—it's feeling like you're the only one.
              It's explaining yourself at every meal. It's the doubt that creeps in when you have no one to ask. It's wondering if maybe everyone else is right and you're making a mistake.
            </p>
            <p className="text-body-lg book-page-body-mb">
              That isolation is what breaks people. Not the diet. The loneliness.
            </p>
            <p className="text-body-lg">
              But what if you had thousands of people who understood? People supporting each other.
              Celebrating wins. Answering questions at 2am. Sharing what works. Your tribe is already here. They're waiting for you.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2>Frequently Asked Questions</h2>
          </div>
          <div className="content-narrow">
            <FAQItem
              question="Is this just another diet book?"
              answer="No. This is a book of stories. You'll follow 12 real people through their complete transformation journeys—from the breaking point to the breakthrough. The lessons emerge from their experiences, not from lectures."
            />
            <FAQItem
              question="How do I access the book?"
              answer="You'll receive instant digital access after purchase. Read on any device."
            />
            <FAQItem
              question="Is this medical advice?"
              answer="No. This is a book of stories and experiences. Always consult with your healthcare provider before making any dietary changes."
            />
            <FAQItem
              question="I've tried everything. Why would this be different?"
              answer="Because this isn't about willpower or discipline. The people in these stories didn't succeed by trying harder—they succeeded by doing something different. Their stories will show you what that looks like."
            />
            <FAQItem
              question="How is the bundle delivered?"
              answer="Instantly. After purchase, you'll receive immediate access to download everything. You can start reading within minutes."
            />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="cta py-responsive">
        <div className="container">
          <div className="content-narrow text-center">
            <h2 className="heading-section book-page-cta-heading">
              You Found Your Tribe
            </h2>
            <p className="text-subtitle mb-responsive book-page-cta-subtitle">
              Stop doing this alone. Start thriving together.
            </p>
            <p className="text-body-lg mb-responsive-lg book-page-cta-body">
              When you join, you get the community, all four books, and everything you need to learn, apply, and succeed—for life.
            </p>
            <Link to="/join" className="btn btn-secondary btn-lg">
              Join the Community Now
            </Link>
            <p className="book-page-cta-fine">
              $97 one-time · Lifetime access · All books included
            </p>
          </div>
        </div>
      </section>
    </>
  );
}

function StoryCard({ name, age, quote }: { name: string; age: string; quote: string }) {
  return (
    <motion.div
      className="card"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
    >
      <div className="card-content">
        <h3 className="book-page-story-heading">
          {name}, {age}
        </h3>
        <p className="book-page-story-quote">
          "{quote}"
        </p>
      </div>
    </motion.div>
  );
}

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={`faq-item ${isOpen ? 'active' : ''}`}>
      <div className="faq-question" onClick={() => setIsOpen(!isOpen)}>
        <span>{question}</span>
        <span className="book-page-faq-icon">{isOpen ? '−' : '+'}</span>
      </div>
      {isOpen && <div className="faq-answer">{answer}</div>}
    </div>
  );
}
